import React from 'react';
import { Alert, Box, Chip, FormHelperText, Grid, IconButton, InputAdornment, InputLabel, LinearProgress, OutlinedInput, Paper, Tab, Tabs, Tooltip } from '@mui/material';
import LinkIcon from '@mui/icons-material/Link';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import FacebookIcon from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';
import YouTubeIcon from '@mui/icons-material/YouTube';
import TwitterIcon from '@mui/icons-material/Twitter';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import PageHeader from '../../../components/page-header';
import { FieldError, FormPageFooter, FormSectionTitle } from '../../../components/form-page-parts';
import { RichTextEditor } from '../components/rich-text-editor';
import { useParametersController } from './context/parameters-controller';
import { useParametersState } from './context/parameters-context';
import { ThemePicker } from './components/theme-picker';
import {
    CONTENT_FIELDS, COVID_URL_FIELDS, isHttpUrl, NEWS_QUANTITY_DEFAULT, NEWS_QUANTITY_MAX, NEWS_QUANTITY_MIN,
    OPERATING_HOURS_MAX, ParametersField, ParametersFieldConfig, SOCIAL_URL_FIELDS, TAB_FIELDS, TRANSPARENCY_URL_FIELDS, validateParameters,
} from './components/parameters-utils';

const SOCIAL_ICONS: Partial<Record<ParametersField, React.ReactNode>> = {
    urlProfileFacebook: <FacebookIcon fontSize="small" />,
    urlProfileInstagram: <InstagramIcon fontSize="small" />,
    urlProfileYoutube: <YouTubeIcon fontSize="small" />,
    urlProfileTwitter: <TwitterIcon fontSize="small" />,
};

const TABS = ['Geral', 'Conteúdo institucional', 'Links de transparência'];

function UrlField({ config, value, error, onChange, icon }: {
    config: ParametersFieldConfig; value: string; error?: string; onChange: (value: string) => void; icon?: React.ReactNode;
}) {
    const url = value.trim();
    return (
        <Grid item xs={12} md={6}>
            <InputLabel htmlFor={`param-${config.field}`}>{config.label}</InputLabel>
            <OutlinedInput
                id={`param-${config.field}`}
                fullWidth
                autoComplete="off"
                type="url"
                placeholder="https://"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                error={!!error}
                inputProps={{ maxLength: config.maxLength }}
                startAdornment={<InputAdornment position="start">{icon || <LinkIcon fontSize="small" />}</InputAdornment>}
                endAdornment={url && isHttpUrl(url) && (
                    <InputAdornment position="end">
                        <Tooltip title="Abrir link em nova aba">
                            <IconButton size="small" edge="end" href={url} target="_blank" rel="noopener noreferrer" aria-label={`Abrir ${config.label}`}>
                                <OpenInNewIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </InputAdornment>
                )}
            />
            <FieldError message={error} />
        </Grid>
    );
}

export function ParametersComponent() {
    const ctrl = useParametersController();
    const state = useParametersState();
    const { form, parameters, showValidationErrors, tab } = state;
    const errors = showValidationErrors ? validateParameters(form) : {};

    const urlField = (config: ParametersFieldConfig, icon?: React.ReactNode) => (
        <UrlField
            key={config.field}
            config={config}
            value={form[config.field]}
            error={errors[config.field]}
            onChange={(value) => ctrl.handleChange(config.field, value)}
            icon={icon}
        />
    );

    return (
        <React.Fragment>
            <Grid item xs={12}>
                <PageHeader
                    title="Parâmetros do Portal"
                    subtitle="Aparência, conteúdo institucional e links externos exibidos no portal da entidade"
                    breadcrumbs={[{ label: 'Parâmetros do Portal' }]}
                    actions={state.isDirty && <Chip color="warning" variant="outlined" label="Alterações não salvas" />}
                />
            </Grid>

            <Grid item xs={12}>
                <Paper sx={{ p: 3 }}>
                    {state.loading && <LinearProgress sx={{ mb: 2 }} />}

                    {parameters && !parameters.exists && (
                        <Alert severity="info" sx={{ mb: 2 }}>
                            A entidade ainda não possui parâmetros cadastrados. Eles serão criados quando você salvar pela primeira vez.
                        </Alert>
                    )}

                    <Tabs
                        value={tab}
                        onChange={(_, value) => ctrl.handleTabChange(value)}
                        variant="scrollable"
                        allowScrollButtonsMobile
                        sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
                    >
                        {TABS.map((label, index) => {
                            const tabHasErrors = TAB_FIELDS[index].some((f) => errors[f]);
                            return (
                                <Tab
                                    key={label}
                                    label={label}
                                    icon={tabHasErrors ? <ErrorOutlineIcon color="error" fontSize="small" /> : undefined}
                                    iconPosition="end"
                                    sx={{ minHeight: 48 }}
                                />
                            );
                        })}
                    </Tabs>

                    {tab === 0 && (
                        <Grid container spacing={3}>
                            <Grid item xs={12}>
                                <FormSectionTitle title="Tema de cores" description="Paleta aplicada ao cabeçalho, botões e destaques do portal" first />
                                <ThemePicker value={form.theme} onChange={(value) => ctrl.handleChange('theme', value)} />
                            </Grid>

                            <Grid item xs={12}>
                                <FormSectionTitle title="Página inicial" />
                                <Grid container spacing={3}>
                                    <Grid item xs={12} md={6}>
                                        <InputLabel htmlFor="param-newsQuantityHome">Quantidade de notícias</InputLabel>
                                        <OutlinedInput
                                            id="param-newsQuantityHome"
                                            fullWidth
                                            type="number"
                                            placeholder={String(NEWS_QUANTITY_DEFAULT)}
                                            value={form.newsQuantityHome}
                                            onChange={(e) => ctrl.handleChange('newsQuantityHome', e.target.value)}
                                            error={!!errors.newsQuantityHome}
                                            inputProps={{ min: NEWS_QUANTITY_MIN, max: NEWS_QUANTITY_MAX, step: 1 }}
                                        />
                                        {errors.newsQuantityHome
                                            ? <FieldError message={errors.newsQuantityHome} />
                                            : <FormHelperText>De {NEWS_QUANTITY_MIN} a {NEWS_QUANTITY_MAX}. Sem valor, o portal exibe {NEWS_QUANTITY_DEFAULT} notícias.</FormHelperText>}
                                    </Grid>
                                </Grid>
                            </Grid>

                            <Grid item xs={12}>
                                <FormSectionTitle title="Atendimento" />
                                <InputLabel htmlFor="param-operatingHours">Horário de funcionamento</InputLabel>
                                <OutlinedInput
                                    id="param-operatingHours"
                                    fullWidth
                                    multiline
                                    minRows={2}
                                    placeholder="Ex.: Segunda a sexta, das 8h às 17h"
                                    value={form.operatingHours}
                                    onChange={(e) => ctrl.handleChange('operatingHours', e.target.value)}
                                    error={!!errors.operatingHours}
                                    inputProps={{ maxLength: OPERATING_HOURS_MAX }}
                                />
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                                    {errors.operatingHours ? <FieldError message={errors.operatingHours} /> : <FormHelperText>Exibido no rodapé do portal</FormHelperText>}
                                    <FormHelperText sx={{ flexShrink: 0 }}>{form.operatingHours.length}/{OPERATING_HOURS_MAX}</FormHelperText>
                                </Box>
                            </Grid>

                            <Grid item xs={12}>
                                <FormSectionTitle title="Redes sociais" description="Perfis oficiais da entidade; apenas os informados aparecem no portal" />
                                <Grid container spacing={3}>
                                    {SOCIAL_URL_FIELDS.map((config) => urlField(config, SOCIAL_ICONS[config.field]))}
                                </Grid>
                            </Grid>
                        </Grid>
                    )}

                    {tab === 1 && (
                        <Grid container spacing={3}>
                            {CONTENT_FIELDS.map((config, index) => (
                                <Grid item xs={12} key={config.field}>
                                    <FormSectionTitle title={config.label} description={config.helperText} first={index === 0} />
                                    <RichTextEditor
                                        value={form[config.field]}
                                        onChange={(html) => ctrl.handleChange(config.field, html)}
                                        minHeight={160}
                                    />
                                </Grid>
                            ))}
                        </Grid>
                    )}

                    {tab === 2 && (
                        <Grid container spacing={3}>
                            <Grid item xs={12}>
                                <FormSectionTitle
                                    title="Sistemas externos"
                                    description="Endereços completos (http:// ou https://) dos sistemas para onde o portal direciona o cidadão"
                                    first
                                />
                                <Grid container spacing={3}>
                                    {TRANSPARENCY_URL_FIELDS.map((config) => urlField(config))}
                                </Grid>
                            </Grid>

                            <Grid item xs={12}>
                                <FormSectionTitle title="COVID-19" description="Links específicos da transparência de recursos da COVID-19" />
                                <Grid container spacing={3}>
                                    {COVID_URL_FIELDS.map((config) => urlField(config))}
                                </Grid>
                            </Grid>
                        </Grid>
                    )}

                    <FormPageFooter
                        cancelLabel="Descartar alterações"
                        onCancel={ctrl.handleDiscard}
                        onSave={ctrl.handleSave}
                        isSaving={ctrl.isSaving}
                        disabled={!state.isDirty || state.loading}
                    />
                </Paper>
            </Grid>
        </React.Fragment>
    );
}
